import { useCallback, useEffect, useRef, useState } from 'react'
import { StaleStateError } from '../api/client'
import type { Conflict } from './useStaleConflict'

export type { Conflict } from './useStaleConflict'

export interface RunOptions {
  /** Turn a non-412 failure into the message shown to the user (default
   *  `String(e)`, the historical rendering every page used). */
  errorMessage?: (e: unknown) => string
}

export interface ActionState {
  /** True while an action is in flight. */
  busy: boolean
  /** The last non-412 failure, or a message set via `setError`. */
  error: string | null
  /** The last 412 — render `<StaleConflictBanner>` from it. Never folded into `error`. */
  conflict: Conflict | null
  /** Run `action` with busy/error bookkeeping. Resolves to its result, or
   *  `undefined` when it failed or was skipped because another run was in
   *  flight (runs never overlap — a double-click starts one action). */
  run: <T>(action: () => Promise<T>, options?: RunOptions) => Promise<T | undefined>
  /** Set (or clear) the error by hand — e.g. a client-side validation message. */
  setError: (message: string | null) => void
  /** Clear the error + conflict (before re-loading after a rebase). */
  clear: () => void
}

/**
 * The busy/error/try-finally idiom every mutating page wrote by hand, as one
 * hook. A 412 `StaleStateError` is routed to `conflict` (not swallowed into a
 * generic error string) so the page can offer the reload banner; anything else
 * becomes `error`. Safe on unmount: a run settling after the component is gone
 * touches no state.
 */
export function useAction(): ActionState {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [conflict, setConflict] = useState<Conflict | null>(null)
  // Refs, not state: the in-flight guard must see a run started in the same
  // tick (state would still read the pre-click value), and the mounted flag
  // must be readable from a promise that settles after unmount.
  const inFlight = useRef(false)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const run = useCallback(
    async <T,>(action: () => Promise<T>, options?: RunOptions): Promise<T | undefined> => {
      if (inFlight.current) return undefined
      inFlight.current = true
      setBusy(true)
      setError(null)
      setConflict(null)
      try {
        return await action()
      } catch (e) {
        if (!mounted.current) return undefined
        if (e instanceof StaleStateError) {
          setConflict({ message: e.serverMessage, serverState: e.currentState })
        } else {
          setError(options?.errorMessage ? options.errorMessage(e) : String(e))
        }
        return undefined
      } finally {
        inFlight.current = false
        if (mounted.current) setBusy(false)
      }
    },
    [],
  )

  const clear = useCallback(() => {
    setError(null)
    setConflict(null)
  }, [])

  return { busy, error, conflict, run, setError, clear }
}
